import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Loader2, CheckCircle2, AlertTriangle, XCircle, Sparkles, Edit3, Plus, Clock, TrendingUp, Star, History } from 'lucide-react';
import { aiService } from '@/services/aiService';
import type { Project, TimesheetEntry, Timesheet } from '@/types/timesheet';
import { toast } from 'sonner';
import { format, startOfWeek, subWeeks } from 'date-fns';
import { cn } from '@/lib/utils';

interface QuickEntryModalEnhancedProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projects: Project[];
  timesheets: Timesheet[];
  currentEmployeeId: string;
  onEntryAdded: (entry: Omit<TimesheetEntry, 'id'>) => void;
}

interface ParsedResult {
  projectId?: string;
  projectName?: string;
  projectCode?: string;
  date?: string;
  dateFormatted?: string;
  hours?: number;
  description?: string;
  confidence: 'high' | 'medium' | 'low';
  warnings?: string[];
  errors?: string[];
  ambiguities?: {
    type: 'project' | 'date' | 'hours';
    message: string;
    options: Array<{ label: string; value: string }>;
  }[];
}

interface FrequentEntry {
  projectId: string;
  projectName: string;
  projectCode: string;
  description: string;
  avgHours: number;
  frequency: number;
  lastUsed: Date;
}

interface QuickSuggestion {
  text: string;
  description: string;
  icon: React.ReactNode;
}

export function QuickEntryModalEnhanced({ 
  open, 
  onOpenChange, 
  projects, 
  timesheets,
  currentEmployeeId,
  onEntryAdded 
}: QuickEntryModalEnhancedProps) {
  const [input, setInput] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [parsedResult, setParsedResult] = useState<ParsedResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);

  // Analyze user's timesheet history to extract patterns
  const userHistory = useMemo(() => {
    const userTimesheets = timesheets.filter(ts => ts.employeeId === currentEmployeeId);
    const recentTimesheets = userTimesheets
      .filter(ts => {
        const weekStart = startOfWeek(ts.weekEnding, { weekStartsOn: 1 });
        const fourWeeksAgo = subWeeks(new Date(), 4);
        return weekStart >= fourWeeksAgo;
      })
      .slice(0, 20); // Last 20 timesheets

    return recentTimesheets;
  }, [timesheets, currentEmployeeId]);

  // Extract frequent entries from user history
  const frequentEntries = useMemo((): FrequentEntry[] => {
    const entryMap = new Map<string, FrequentEntry>();

    userHistory.forEach(timesheet => {
      timesheet.entries.forEach(entry => {
        const project = projects.find(p => p.id === entry.projectId);
        if (!project) return;

        const key = `${entry.projectId}-${entry.description}`;
        const totalHours = Object.values(entry.hours).reduce((sum, h) => sum + h, 0);

        if (entryMap.has(key)) {
          const existing = entryMap.get(key)!;
          existing.frequency += 1;
          existing.avgHours = (existing.avgHours * (existing.frequency - 1) + totalHours) / existing.frequency;
          existing.lastUsed = new Date(Math.max(existing.lastUsed.getTime(), timesheet.weekEnding.getTime()));
        } else {
          entryMap.set(key, {
            projectId: entry.projectId,
            projectName: project.name,
            projectCode: project.code,
            description: entry.description,
            avgHours: totalHours,
            frequency: 1,
            lastUsed: timesheet.weekEnding,
          });
        }
      });
    });

    // Sort by frequency and recency
    return Array.from(entryMap.values())
      .sort((a, b) => {
        const freqDiff = b.frequency - a.frequency;
        if (freqDiff !== 0) return freqDiff;
        return b.lastUsed.getTime() - a.lastUsed.getTime();
      })
      .slice(0, 5);
  }, [userHistory, projects]);

  // Get most used projects
  const topProjects = useMemo(() => {
    const projectFrequency = new Map<string, { project: Project; count: number; lastUsed: Date }>();

    userHistory.forEach(timesheet => {
      timesheet.entries.forEach(entry => {
        const project = projects.find(p => p.id === entry.projectId);
        if (!project) return;

        if (projectFrequency.has(entry.projectId)) {
          const existing = projectFrequency.get(entry.projectId)!;
          existing.count += 1;
          existing.lastUsed = new Date(Math.max(existing.lastUsed.getTime(), timesheet.weekEnding.getTime()));
        } else {
          projectFrequency.set(entry.projectId, {
            project,
            count: 1,
            lastUsed: timesheet.weekEnding,
          });
        }
      });
    });

    return Array.from(projectFrequency.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 5)
      .map(item => item.project);
  }, [userHistory, projects]);

  // Get common activity descriptions
  const commonActivities = useMemo(() => {
    const activityMap = new Map<string, number>();

    userHistory.forEach(timesheet => {
      timesheet.entries.forEach(entry => {
        const desc = entry.description.trim();
        if (desc && desc.length > 3) {
          activityMap.set(desc, (activityMap.get(desc) || 0) + 1);
        }
      });
    });

    return Array.from(activityMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([desc]) => desc);
  }, [userHistory]);

  // Generate smart suggestions based on patterns
  const smartSuggestions = useMemo((): QuickSuggestion[] => {
    const suggestions: QuickSuggestion[] = [];

    // Suggestion from most frequent entry
    if (frequentEntries.length > 0) {
      const top = frequentEntries[0];
      suggestions.push({
        text: `${Math.round(top.avgHours)}h on ${top.projectCode} ${top.description}`,
        description: `Used ${top.frequency} times recently`,
        icon: <TrendingUp className="h-4 w-4 text-blue-600" />,
      });
    }

    // Suggestion from favorite project
    const favoriteProject = projects.find(p => p.isFavourite);
    if (favoriteProject && frequentEntries.length > 1) {
      const entry = frequentEntries[1];
      suggestions.push({
        text: `8h on ${favoriteProject.code} Development`,
        description: 'Your favorite project',
        icon: <Star className="h-4 w-4 text-amber-600" />,
      });
    }

    // Suggestion from recent activity
    if (frequentEntries.length > 2) {
      const recent = frequentEntries[2];
      suggestions.push({
        text: `${Math.round(recent.avgHours)}h on ${recent.projectCode} ${recent.description}`,
        description: `Last used ${format(recent.lastUsed, 'MMM d')}`,
        icon: <History className="h-4 w-4 text-purple-600" />,
      });
    }

    return suggestions;
  }, [frequentEntries, projects]);

  // Debounced parsing with context-aware validation
  useEffect(() => {
    if (!input.trim() || !open) {
      setParsedResult(null);
      setShowSuggestions(true);
      return;
    }

    setShowSuggestions(false);
    setIsParsing(true);
    const timer = setTimeout(async () => {
      try {
        const result = await aiService.parseTimeEntryWithValidation(input, projects);
        
        // Enhance result with historical context
        if (result.projectId && result.description) {
          const similarEntry = frequentEntries.find(
            fe => fe.projectId === result.projectId && 
                  fe.description.toLowerCase().includes(result.description!.toLowerCase())
          );
          
          if (similarEntry) {
            result.warnings = result.warnings || [];
            result.warnings.push(
              `Similar entry found: Usually ${similarEntry.avgHours.toFixed(1)}h for this activity`
            );
          }
        }
        
        setParsedResult(result);
      } catch (error) {
        console.error('Parse error:', error);
        setParsedResult({
          confidence: 'low',
          errors: ['Failed to parse input. Please try again.'],
        });
      } finally {
        setIsParsing(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [input, projects, open, frequentEntries]);

  const handleSubmit = useCallback(async () => {
    if (!parsedResult || parsedResult.confidence === 'low' || !parsedResult.projectId || !parsedResult.hours) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Convert date to day of week for hours object
      const targetDate = parsedResult.date ? new Date(parsedResult.date) : new Date();
      const dayOfWeek = targetDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const targetDay = dayNames[dayOfWeek];
      
      // Find the project to get the first cost centre
      const project = projects.find(p => p.id === parsedResult.projectId);
      
      // Create entry with hours object (TimesheetEntry format)
      const entry: Omit<TimesheetEntry, 'id'> = {
        projectId: parsedResult.projectId,
        costCentreId: project?.costCentres[0]?.id || '',
        description: parsedResult.description || 'Time entry',
        hours: {
          monday: targetDay === 'monday' ? parsedResult.hours : 0,
          tuesday: targetDay === 'tuesday' ? parsedResult.hours : 0,
          wednesday: targetDay === 'wednesday' ? parsedResult.hours : 0,
          thursday: targetDay === 'thursday' ? parsedResult.hours : 0,
          friday: targetDay === 'friday' ? parsedResult.hours : 0,
          saturday: targetDay === 'saturday' ? parsedResult.hours : 0,
          sunday: targetDay === 'sunday' ? parsedResult.hours : 0,
        },
      };

      onEntryAdded(entry);
      
      // Show success toast
      toast.success('Entry added successfully', {
        description: `Added ${parsedResult.hours}h to ${parsedResult.projectName} on ${parsedResult.dateFormatted}`,
      });

      // Reset and close
      setInput('');
      setParsedResult(null);
      setShowSuggestions(true);
      onOpenChange(false);
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to add entry', {
        description: 'Please try again or use the manual entry form.',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [parsedResult, onEntryAdded, onOpenChange, projects]);

  const handleSuggestionClick = useCallback((suggestionText: string) => {
    setInput(suggestionText);
    setShowSuggestions(false);
  }, []);

  const handleAmbiguitySelection = useCallback((type: string, value: string) => {
    if (type === 'project' && parsedResult) {
      const projectMatch = value.match(/^(.+?)\s*\(([^)]+)\)$/);
      if (projectMatch) {
        const [, projectName, projectCode] = projectMatch;
        const selectedProject = projects.find(
          p => p.name === projectName.trim() && p.code === projectCode.trim()
        );
        
        if (selectedProject) {
          setParsedResult({
            ...parsedResult,
            projectId: selectedProject.id,
            projectName: selectedProject.name,
            projectCode: selectedProject.code,
            confidence: 'high',
            ambiguities: [],
          });
          
          toast.success('Project selected', {
            description: `${selectedProject.name} (${selectedProject.code})`,
          });
        }
      }
    } else if (type === 'date' && parsedResult) {
      const isLastWeek = value.toLowerCase().includes('last');
      const targetDay = parsedResult.dateFormatted?.split(',')[0].toLowerCase();
      
      if (targetDay) {
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayIndex = days.indexOf(targetDay);
        
        if (dayIndex !== -1) {
          const currentDate = new Date();
          const currentDayIndex = currentDate.getDay();
          let targetDate = new Date(currentDate);
          
          let dayDiff = dayIndex - currentDayIndex;
          if (isLastWeek) {
            dayDiff -= 7;
          }
          
          targetDate.setDate(targetDate.getDate() + dayDiff);
          
          setParsedResult({
            ...parsedResult,
            date: format(targetDate, 'yyyy-MM-dd'),
            dateFormatted: format(targetDate, 'EEE, MMM d'),
            confidence: 'high',
            ambiguities: parsedResult.ambiguities?.filter(a => a.type !== 'date') || [],
          });
          
          toast.success('Date selected', {
            description: format(targetDate, 'EEEE, MMMM d, yyyy'),
          });
        }
      }
    }
  }, [parsedResult, projects]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setInput('');
      setParsedResult(null);
      setIsParsing(false);
      setIsSubmitting(false);
      setShowSuggestions(true);
    }
  }, [open]);

  const getConfidenceIcon = () => {
    if (!parsedResult) return null;
    
    switch (parsedResult.confidence) {
      case 'high':
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case 'medium':
        return <AlertTriangle className="h-5 w-5 text-amber-600" />;
      case 'low':
        return <XCircle className="h-5 w-5 text-red-600" />;
    }
  };

  const getConfidenceLabel = () => {
    if (!parsedResult) return null;
    
    switch (parsedResult.confidence) {
      case 'high':
        return 'Understood';
      case 'medium':
        return 'Please confirm';
      case 'low':
        return "Couldn't understand";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Quick Entry
          </DialogTitle>
          <DialogDescription>
            Describe your time entry in natural language. We'll learn from your patterns.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {/* Input Field */}
          <div className="space-y-2">
            <Label htmlFor="quick-entry-input">What did you work on?</Label>
            <div className="relative">
              <Input
                id="quick-entry-input"
                placeholder='e.g., "8h on Phoenix yesterday" or "4 hours client meeting today"'
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && parsedResult?.confidence === 'high') {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                className="pr-10"
                autoFocus
              />
              {isParsing && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
          </div>

          {/* Smart Suggestions */}
          {showSuggestions && !input && smartSuggestions.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Sparkles className="h-4 w-4" />
                Smart Suggestions
              </div>
              <div className="space-y-2">
                {smartSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion.text)}
                    className="w-full text-left p-3 rounded-lg border bg-card hover:bg-accent transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">{suggestion.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{suggestion.text}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {suggestion.description}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Frequent Entries Reference */}
          {showSuggestions && !input && frequentEntries.length > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  Your Recent Patterns
                </div>
                <ScrollArea className="h-[120px]">
                  <div className="space-y-1.5">
                    {frequentEntries.map((entry, index) => (
                      <div
                        key={index}
                        className="text-xs p-2 rounded bg-muted/50 flex items-center justify-between"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">
                            {entry.projectName} ({entry.projectCode})
                          </div>
                          <div className="text-muted-foreground truncate">
                            {entry.description}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <Badge variant="secondary" className="text-xs">
                            ~{entry.avgHours.toFixed(1)}h
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {entry.frequency}x
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </>
          )}

          {/* Common Activities */}
          {showSuggestions && !input && commonActivities.length > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">
                  Common Activities
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {commonActivities.map((activity, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="text-xs cursor-pointer hover:bg-accent"
                      onClick={() => setInput(activity)}
                    >
                      {activity}
                    </Badge>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Top Projects */}
          {showSuggestions && !input && topProjects.length > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">
                  Your Top Projects
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {topProjects.map((project) => (
                    <Badge
                      key={project.id}
                      variant="secondary"
                      className="text-xs cursor-pointer hover:bg-accent"
                      onClick={() => setInput(`8h on ${project.code}`)}
                    >
                      {project.isFavourite && <Star className="h-3 w-3 mr-1 fill-current" />}
                      {project.name} ({project.code})
                    </Badge>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Parsing Indicator */}
          {isParsing && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>Analyzing your input...</AlertDescription>
            </Alert>
          )}

          {/* Parsed Result Preview */}
          {!isParsing && parsedResult && (
            <div className="space-y-3">
              {/* Confidence Header */}
              <div className="flex items-center gap-2">
                {getConfidenceIcon()}
                <span className="font-medium text-sm">{getConfidenceLabel()}</span>
              </div>

              {/* High Confidence - Show parsed fields */}
              {parsedResult.confidence === 'high' && (
                <div className="rounded-lg border bg-green-50 dark:bg-green-950/20 p-4 space-y-2">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {parsedResult.projectName && (
                      <div>
                        <span className="text-muted-foreground">Project:</span>
                        <p className="font-medium">
                          {parsedResult.projectName}
                          {parsedResult.projectCode && (
                            <span className="text-muted-foreground ml-1">({parsedResult.projectCode})</span>
                          )}
                        </p>
                      </div>
                    )}
                    {parsedResult.dateFormatted && (
                      <div>
                        <span className="text-muted-foreground">Date:</span>
                        <p className="font-medium">{parsedResult.dateFormatted}</p>
                      </div>
                    )}
                    {parsedResult.hours !== undefined && (
                      <div>
                        <span className="text-muted-foreground">Hours:</span>
                        <p className="font-medium">{parsedResult.hours.toFixed(1)}</p>
                      </div>
                    )}
                  </div>
                  {parsedResult.description && (
                    <div className="text-sm pt-2 border-t">
                      <span className="text-muted-foreground">Description:</span>
                      <p className="font-medium mt-1">{parsedResult.description}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Medium Confidence - Show ambiguities */}
              {parsedResult.confidence === 'medium' && parsedResult.ambiguities && (
                <div className="space-y-3">
                  {parsedResult.ambiguities.map((ambiguity, index) => (
                    <Alert key={index} variant="default" className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <AlertDescription>
                        <p className="font-medium mb-2">{ambiguity.message}</p>
                        <div className="space-y-1">
                          {ambiguity.options.map((option, optIndex) => (
                            <Button
                              key={optIndex}
                              variant="outline"
                              size="sm"
                              className="w-full justify-start"
                              onClick={() => handleAmbiguitySelection(ambiguity.type, option.value)}
                            >
                              {option.label}
                            </Button>
                          ))}
                        </div>
                      </AlertDescription>
                    </Alert>
                  ))}
                </div>
              )}

              {/* Low Confidence - Show errors */}
              {parsedResult.confidence === 'low' && parsedResult.errors && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>
                    <p className="font-medium mb-2">{parsedResult.errors[0]}</p>
                    <p className="text-xs mt-2">
                      Try using one of the suggestions above or: "8h on {topProjects[0]?.code || 'PROJECT'} yesterday"
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              {/* Warnings */}
              {parsedResult.warnings && parsedResult.warnings.length > 0 && (
                <div className="space-y-2">
                  {parsedResult.warnings.map((warning, index) => (
                    <Alert key={index} variant="default" className="border-blue-200 bg-blue-50 dark:bg-blue-950/20">
                      <AlertTriangle className="h-4 w-4 text-blue-600" />
                      <AlertDescription className="text-sm">{warning}</AlertDescription>
                    </Alert>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Example hints when no input */}
          {!input && !showSuggestions && (
            <div className="text-xs text-muted-foreground space-y-1 p-3 bg-muted/50 rounded-lg">
              <p className="font-medium">Quick Entry Examples:</p>
              <ul className="list-disc list-inside space-y-0.5 ml-2">
                <li>"8h on Phoenix yesterday"</li>
                <li>"4 hours client meeting today"</li>
                <li>"6.5 hrs coding on HSBC Cloud Migration Monday"</li>
                <li>"3h Development on {topProjects[0]?.code || 'PROJECT'} Tuesday"</li>
              </ul>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          
          {parsedResult && parsedResult.confidence !== 'low' && (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || parsedResult.confidence === 'medium'}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add to Timesheet
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
